import { ReactNode } from 'react';
import { Button, MenuItem, Stack, TextField } from '@mui/material';
import { FinanceFilters } from '../../types/finance';
import { useFinanceLookups } from './useFinanceLookups';

export type FilterField = 'dates' | 'university' | 'intake' | 'category';

interface Props {
  value: FinanceFilters;
  onChange: (next: FinanceFilters) => void;
  /** Which filters this page supports (default: all). */
  fields?: FilterField[];
  /** Page-specific controls (search, status…) shown first. */
  children?: ReactNode;
}

const ALL_FIELDS: FilterField[] = ['dates', 'university', 'intake', 'category'];

/** The Finance workspace's shared filter row. Branch isn't here: a Super
 * Admin uses the header's branch selector (like every other page), and a
 * Finance Admin is always held to their own branch by the server. */
export const FinanceFilterBar = ({ value, onChange, fields = ALL_FIELDS, children }: Props) => {
  const lookups = useFinanceLookups();
  const set = (patch: Partial<FinanceFilters>) => onChange({ ...value, ...patch });
  const idValue = (v: number | undefined) => (v == null ? '' : String(v));
  const toId = (v: string) => (v === '' ? undefined : Number(v));
  const has = (f: FilterField) => fields.includes(f);
  const active =
    !!value.from ||
    !!value.to ||
    !!value.universityId ||
    !!value.intakeId ||
    !!value.paymentCategoryId;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ mb: 2 }}
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
    >
      {children}
      {has('dates') && (
        <>
          <TextField
            type="date"
            size="small"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={value.from ?? ''}
            onChange={(e) => set({ from: e.target.value || undefined })}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={value.to ?? ''}
            onChange={(e) => set({ to: e.target.value || undefined })}
          />
        </>
      )}
      {has('university') && (
        <TextField
          select
          size="small"
          label="University"
          sx={{ minWidth: 190 }}
          value={idValue(value.universityId)}
          onChange={(e) => set({ universityId: toId(e.target.value) })}
        >
          <MenuItem value="">All universities</MenuItem>
          {lookups.data?.universities.map((u) => (
            <MenuItem key={u.id} value={String(u.id)}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {has('intake') && (
        <TextField
          select
          size="small"
          label="Intake"
          sx={{ minWidth: 150 }}
          value={idValue(value.intakeId)}
          onChange={(e) => set({ intakeId: toId(e.target.value) })}
        >
          <MenuItem value="">All intakes</MenuItem>
          {lookups.data?.intakes.map((i) => (
            <MenuItem key={i.id} value={String(i.id)}>
              {i.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {has('category') && (
        <TextField
          select
          size="small"
          label="Payment category"
          sx={{ minWidth: 200 }}
          value={idValue(value.paymentCategoryId)}
          onChange={(e) => set({ paymentCategoryId: toId(e.target.value) })}
        >
          <MenuItem value="">All categories</MenuItem>
          {lookups.data?.paymentCategories.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {active && (
        <Button
          size="small"
          onClick={() =>
            onChange({
              branchId: value.branchId,
            })
          }
        >
          Clear filters
        </Button>
      )}
    </Stack>
  );
};
